import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  Pagination,
  IconButton,
  Typography,
  Avatar,
  useMediaQuery,
  useTheme,
  Drawer,
  CircularProgress,
  Alert,
  Snackbar,
  Button,
  TextField,
  InputAdornment,
  FormControl,
  Select,
  MenuItem,
  SelectChangeEvent,
  Chip,
  Grid,
  Stack,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Switch,
  Badge,
  Autocomplete,
} from '@mui/material';
import {
  Delete,
  Add,
  Search,
  FilterList,
  ArrowBack,
  Close,
  LocalOffer,
  CalendarToday,
  AttachMoney,
  Refresh,
  Visibility,
  Whatshot,
} from '@mui/icons-material';

import Slidemenu from '../../components/admin/adminSlidemenu.tsx';
import AdminHeader from '../../components/admin/adminHeader.tsx';

import {
  useListFlashSalesQuery,
  useAddFlashSaleMutation,
  useDeleteFlashSaleMutation,
} from '../../features/admin/flashSaleApi.ts';

import {
  useListProductsQuery,
  Product,
} from '../../features/admin/productApi.ts';

interface FlashSale {
  id: number;
  product_id: string | number;
  product_price: string;
  start_date: string;
  end_date: string;
  status: string;
  sale_status?: string;
}

interface EnrichedFlashSale extends FlashSale {
  product_name?: string;
  product_image?: string;
  prod_name?: string;
  product_main_image?: string;
  original_price?: number;
  currency?: string;
}

interface FlashSaleFormData {
  product_id: string;
  product_price: string;
  start_date: string;
  end_date: string;
  active: boolean;
}

const FlashSale = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [mobileOpen, setMobileOpen] = useState(false);
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [productSearchStr, setProductSearchStr] = useState('');
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [enrichedFlashSales, setEnrichedFlashSales] = useState<EnrichedFlashSale[]>([]);
  const [formData, setFormData] = useState<FlashSaleFormData>({
    product_id: '',
    product_price: '',
    start_date: '',
    end_date: '',
    active: true,
  });
  const [formErrors, setFormErrors] = useState<Partial<FlashSaleFormData>>({});
  const [snackbar, setSnackbar] = useState({ 
    open: false, 
    message: '', 
    severity: 'success' as 'success' | 'error' 
  });
  const [tabValue, setTabValue] = useState(0);

  // API hooks
  const { 
    data, 
    isLoading, 
    error, 
    isError, 
    refetch 
  } = useListFlashSalesQuery({});

  const {
    data: productsResponse,
    isLoading: isLoadingProducts,
  } = useListProductsQuery({
    page: 1,
    limit: 50,
    search_str: productSearchStr
  });

  // Fetch all products to enrich flash sales with product details
  const {
    data: allProductsResponse,
    isLoading: isLoadingAllProducts,
  } = useListProductsQuery({
    page: 1,
    limit: 1000,
    search_str: ''
  });

  const [addFlashSale, { isLoading: isAdding }] = useAddFlashSaleMutation();
  const [deleteFlashSale, { isLoading: isDeleting }] = useDeleteFlashSaleMutation();

  // Handle API response structure
  const flashSales = data?.data || [];
  const products = productsResponse?.result || productsResponse?.data || [];
  const allProducts = allProductsResponse?.result || allProductsResponse?.data || [];
  const totalRecords = enrichedFlashSales.length;

  // Enrich flash sales with product details
  useEffect(() => {
    if (flashSales.length > 0 && allProducts.length > 0) {
      const enriched = flashSales.map((sale: FlashSale) => {
        const productId = typeof sale.product_id === 'string' ? parseInt(sale.product_id) : sale.product_id;
        const product = allProducts.find((p: Product) => 
          (p.product_id === productId || p.id === productId)
        );

        return {
          ...sale,
          product_name: product?.prod_name || 'Unknown Product',
          product_image: product?.product_main_image || '',
          prod_name: product?.prod_name || '',
          product_main_image: product?.product_main_image || '',
          original_price: product?.product_price || 0,
          currency: product?.currency || 'AED',
        };
      });
      setEnrichedFlashSales(enriched);
    } else if (flashSales.length === 0) {
      setEnrichedFlashSales([]);
    }
  }, [flashSales, allProducts]);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleRefresh = () => {
    refetch();
    setSnackbar({
      open: true,
      message: 'Flash sales refreshed successfully!',
      severity: 'success'
    });
  };

  const handleOpenAddDialog = () => {
    setOpenAddDialog(true);
    setSelectedProduct(null);
    setProductSearchStr('');
    setFormData({
      product_id: '',
      product_price: '',
      start_date: '',
      end_date: '',
      active: true,
    });
    setFormErrors({});
  };

  const handleCloseAddDialog = () => {
    setOpenAddDialog(false);
    setSelectedProduct(null);
    setProductSearchStr('');
    setFormData({
      product_id: '',
      product_price: '',
      start_date: '',
      end_date: '',
      active: true,
    });
    setFormErrors({});
  };

  const handleProductSelect = (event: any, value: Product | null) => {
    setSelectedProduct(value);
    if (value) {
      setFormData(prev => ({
        ...prev,
        product_id: (value.product_id || value.id || '').toString(),
        product_price: value.product_price?.toString() || '',
      }));
      if (formErrors.product_id) {
        setFormErrors(prev => ({
          ...prev,
          product_id: undefined
        }));
      }
    } else {
      setFormData(prev => ({
        ...prev,
        product_id: '',
        product_price: '',
      }));
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (formErrors[name as keyof FlashSaleFormData]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: undefined
      }));
    }
  };

  const handleActiveChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({
      ...prev,
      active: e.target.checked
    }));
  };

  const validateForm = (): boolean => {
    const errors: Partial<FlashSaleFormData> = {};

    if (!formData.product_id.trim()) {
      errors.product_id = 'Product is required';
    }
    if (!formData.start_date) {
      errors.start_date = 'Start date is required';
    }
    if (!formData.end_date) {
      errors.end_date = 'End date is required';
    }
    if (formData.start_date && formData.end_date && new Date(formData.end_date) <= new Date(formData.start_date)) {
      errors.end_date = 'End date must be after start date';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleAddFlashSale = async () => {
    if (!validateForm()) {
      return;
    }

    if (!selectedProduct) {
      return;
    }

    try {
      const payload = {
        product_id: Number(formData.product_id),
        product_price: selectedProduct.product_price || 0,
        start_date: formData.start_date,
        end_date: formData.end_date,
        active: formData.active ? 1 : 0,
      };

      await addFlashSale(payload).unwrap();
      setSnackbar({
        open: true,
        message: 'Flash sale added successfully!',
        severity: 'success'
      });
      handleCloseAddDialog();
      refetch();
    } catch (err: any) {
      console.error('Failed to add flash sale:', err);
      setSnackbar({
        open: true,
        message: err?.data?.message || 'Error adding flash sale. Please try again.',
        severity: 'error'
      });
    }
  };

  const handleDelete = async (flash_id: number) => {
    if (window.confirm('Are you sure you want to delete this flash sale?')) {
      try {
        await deleteFlashSale(flash_id).unwrap();
        setSnackbar({
          open: true,
          message: 'Flash sale deleted successfully!',
          severity: 'success'
        });
        refetch();
      } catch (err: any) {
        console.error('Failed to delete flash sale:', err);
        setSnackbar({
          open: true,
          message: err?.data?.message || 'Error deleting flash sale. Please try again.',
          severity: 'error'
        });
      }
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const formatDateTime = (dateString: string) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusColor = (status: string) => {
    const statusLower = status?.toLowerCase();
    if (statusLower === 'active') {
      return { bgcolor: '#d1f4e0', color: '#00783e', fontWeight: 600 };
    } else if (statusLower === 'upcoming') {
      return { bgcolor: '#e3f2fd', color: '#1565c0', fontWeight: 600 };
    } else if (statusLower === 'expired') {
      return { bgcolor: '#ffcdd2', color: '#c62828', fontWeight: 600 };
    }
    return { bgcolor: '#e4e5e7', color: '#616161', fontWeight: 600 };
  };

  const formatCurrency = (amount: number | string) => {
    const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'AED',
    }).format(numAmount);
  };

  const calculateDiscount = (original: number, sale: number) => {
    if (!original || original <= 0) return 0;
    return Math.round(((original - sale) / original) * 100);
  };

  const isLoadingData = isLoading || isLoadingAllProducts;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', bgcolor: '#f1f2f4' }}>
      <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
        <Box sx={{ width: '250px', flexShrink: 0, display: { xs: 'none', md: 'block' } }}>
          <Slidemenu mobileOpen={false} onToggle={handleDrawerToggle} />
        </Box>
        
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': { width: 250 },
          }}
        >
          <Slidemenu onToggle={handleDrawerToggle} mobileOpen={false} />
        </Drawer>
        
        <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <AdminHeader onToggleMenu={handleDrawerToggle} />
          <Box
            sx={{
              flexGrow: 1,
              overflow: 'auto',
              backgroundColor: '#f1f2f4',
            }}
          >
            <Box sx={{ maxWidth: 1600, mx: 'auto', p: 3 }}>
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                mb: 2
              }}>
                <Typography variant="h5" sx={{ fontWeight: 600, color: '#202223' }}>
                  Flash Sales
                </Typography>
                
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    variant="outlined"
                    startIcon={<Refresh />}
                    onClick={handleRefresh}
                    disabled={isLoadingData}
                    sx={{
                      textTransform: 'none',
                      borderColor: '#c9cccf',
                      color: '#202223',
                      borderRadius: '8px',
                      fontWeight: 500,
                      '&:hover': {
                        borderColor: '#202223',
                        bgcolor: 'transparent',
                      }
                    }}
                  >
                    Refresh
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={<Add />}
                    onClick={handleOpenAddDialog}
                    sx={{
                      bgcolor: '#008060',
                      color: 'white',
                      textTransform: 'none',
                      fontWeight: 600,
                      borderRadius: '8px',
                      px: 2.5,
                      '&:hover': { bgcolor: '#006e52' }
                    }}
                  >
                    Add Flash Sale
                  </Button>
                </Box>
              </Box>

              <Card sx={{ borderRadius: '16px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', border: '1px solid #e1e3e5', overflow: 'hidden' }}>
                <Box sx={{ borderBottom: '1px solid #e1e3e5' }}>
                  <Tabs 
                    value={tabValue} 
                    onChange={(e, v) => setTabValue(v)}
                    sx={{
                      px: 2,
                      '& .MuiTab-root': {
                        textTransform: 'none',
                        fontWeight: 500,
                        fontSize: '14px',
                        minHeight: 48,
                        color: '#6d7175',
                      },
                      '& .Mui-selected': {
                        color: '#202223',
                        fontWeight: 600,
                      },
                      '& .MuiTabs-indicator': {
                        backgroundColor: '#008060',
                        height: 3,
                      }
                    }}
                  >
                    <Tab label={`All flash sales (${totalRecords})`} />
                    <Tab label="Active" />
                    <Tab label="Upcoming" />
                    <Tab label="Expired" />
                  </Tabs>
                </Box>

                <Box sx={{ p: 2, borderBottom: '1px solid #e1e3e5', display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <TextField
                    size="small"
                    placeholder="Search flash sales..."
                    sx={{ 
                      minWidth: 300,
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '8px',
                        bgcolor: 'white',
                      }
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Search fontSize="small" sx={{ color: '#6d7175' }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                  <Button
                    variant="outlined"
                    startIcon={<FilterList />}
                    sx={{
                      textTransform: 'none',
                      borderColor: '#c9cccf',
                      color: '#202223',
                      borderRadius: '8px',
                      fontWeight: 500,
                      '&:hover': {
                        borderColor: '#202223',
                        bgcolor: 'transparent',
                      }
                    }}
                  >
                    More filters
                  </Button>
                </Box>

                {isLoadingData && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300, bgcolor: 'white' }}>
                    <CircularProgress size={32} sx={{ color: '#008060' }} />
                  </Box>
                )}

                {isError && !isLoadingData && (
                  <Box sx={{ p: 3, bgcolor: 'white' }}>
                    <Alert severity="error">
                      Failed to load flash sales. Please try again.
                      <Button 
                        color="inherit" 
                        size="small" 
                        onClick={() => refetch()}
                        sx={{ ml: 1 }}
                      >
                        Retry
                      </Button>
                    </Alert>
                  </Box>
                )}

                {!isLoadingData && !isError && enrichedFlashSales.length === 0 && (
                  <Box sx={{ textAlign: 'center', py: 8, bgcolor: 'white' }}>
                    <LocalOffer sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                    <Typography variant="h6" color="text.secondary" gutterBottom sx={{ fontWeight: 500 }}>
                      No flash sales found
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                      Get started by adding your first flash sale
                    </Typography>
                    <Button
                      variant="contained"
                      onClick={handleOpenAddDialog}
                      sx={{
                        bgcolor: '#008060',
                        textTransform: 'none',
                        fontWeight: 600,
                        '&:hover': { bgcolor: '#006e52' }
                      }}
                    >
                      Add Flash Sale
                    </Button>
                  </Box>
                )}

                {!isLoadingData && enrichedFlashSales.length > 0 && (
                  <Box sx={{ bgcolor: 'white' }}>
                    {/* Table Header */}
                    <Box sx={{ 
                      display: 'grid', 
                      gridTemplateColumns: isMobile ? '1fr auto' : '60px 80px 2fr 1fr 1fr 1fr 1fr auto',
                      gap: 2,
                      px: 2,
                      py: 1.5,
                      borderBottom: '1px solid #e1e3e5',
                      bgcolor: '#fafbfb'
                    }}>
                      {!isMobile && (
                        <>
                          <Typography variant="caption" sx={{ color: '#6d7175', fontWeight: 600, fontSize: '11px', textTransform: 'uppercase' }}>
                            #
                          </Typography>
                          <Typography variant="caption" sx={{ color: '#6d7175', fontWeight: 600, fontSize: '11px', textTransform: 'uppercase' }}>
                            Image
                          </Typography>
                          <Typography variant="caption" sx={{ color: '#6d7175', fontWeight: 600, fontSize: '11px', textTransform: 'uppercase' }}>
                            Product
                          </Typography>
                          <Typography variant="caption" sx={{ color: '#6d7175', fontWeight: 600, fontSize: '11px', textTransform: 'uppercase' }}>
                            Price
                          </Typography>
                          <Typography variant="caption" sx={{ color: '#6d7175', fontWeight: 600, fontSize: '11px', textTransform: 'uppercase' }}>
                            Sale Price
                          </Typography>
                          <Typography variant="caption" sx={{ color: '#6d7175', fontWeight: 600, fontSize: '11px', textTransform: 'uppercase' }}>
                            Period
                          </Typography>
                          <Typography variant="caption" sx={{ color: '#6d7175', fontWeight: 600, fontSize: '11px', textTransform: 'uppercase' }}>
                            Status
                          </Typography>
                          <Typography variant="caption" sx={{ color: '#6d7175', fontWeight: 600, fontSize: '11px', textTransform: 'uppercase', textAlign: 'right' }}>
                            Actions
                          </Typography>
                        </>
                      )}
                    </Box>

                    {/* Table Rows */}
                    {enrichedFlashSales.map((sale: EnrichedFlashSale, index: number) => (
                      <Box
                        key={sale.id}
                        sx={{
                          display: 'grid',
                          gridTemplateColumns: isMobile ? '1fr auto' : '60px 80px 2fr 1fr 1fr 1fr 1fr auto',
                          gap: 2,
                          px: 2,
                          py: 2,
                          alignItems: 'center',
                          borderBottom: index < enrichedFlashSales.length - 1 ? '1px solid #e1e3e5' : 'none',
                          '&:hover': { bgcolor: '#f9fafb' },
                          transition: 'background-color 0.15s ease',
                        }}
                      >
                        {!isMobile ? (
                          <>
                            <Typography variant="body2" sx={{ color: '#6d7175' }}>
                              {index + 1}
                            </Typography>

                            <Avatar
                              src={sale.product_image || sale.product_main_image ? 
                                `https://fftestservice.sarsetech.com/images/${sale.product_image || sale.product_main_image}` : undefined}
                              variant="rounded"
                              sx={{ width: 48, height: 48, bgcolor: '#f1f2f4' }}
                            >
                              {(sale.product_name || sale.prod_name)?.[0] || '?'}
                            </Avatar>

                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 600, color: '#202223', mb: 0.5 }}>
                                {sale.product_name || sale.prod_name || 'Product'}
                              </Typography>
                              <Chip 
                                label={`ID: ${sale.product_id}`}
                                size="small"
                                sx={{ 
                                  bgcolor: '#e3f2fd',
                                  color: '#1565c0',
                                  fontWeight: 600,
                                  fontSize: '11px'
                                }}
                              />
                            </Box>

                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 600, color: '#202223' }}>
                                {formatCurrency(sale.original_price || 0)}
                              </Typography>
                            </Box>

                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 600, color: '#2e7d32' }}>
                                {formatCurrency(sale.product_price)}
                              </Typography>
                              {sale.original_price && sale.original_price > 0 && (
                                <Chip
                                  label={`-${calculateDiscount(sale.original_price, parseFloat(sale.product_price))}%`}
                                  size="small"
                                  sx={{ 
                                    bgcolor: '#ffebee',
                                    color: '#c62828',
                                    fontWeight: 600,
                                    fontSize: '11px',
                                    height: 20
                                  }}
                                />
                              )}
                            </Box>

                            <Box>
                              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '12px' }}>
                                {formatDateTime(sale.start_date)}
                              </Typography>
                              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '12px' }}>
                                to {formatDateTime(sale.end_date)}
                              </Typography>
                            </Box>

                            <Box>
                              <Chip 
                                label={sale.sale_status || sale.status || 'Unknown'} 
                                size="small"
                                sx={{
                                  fontSize: '12px',
                                  ...getStatusColor(sale.sale_status || sale.status)
                                }}
                              />
                            </Box>

                            <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                              <IconButton
                                size="small"
                                onClick={() => handleDelete(sale.id)}
                                disabled={isDeleting}
                                sx={{ 
                                  color: '#6d7175',
                                  '&:hover': { bgcolor: '#f1f2f4' }
                                }}
                              >
                                <Delete fontSize="small" />
                              </IconButton>
                            </Box>
                          </>
                        ) : (
                          // Mobile View
                          <>
                            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                              <Avatar
                                src={sale.product_image || sale.product_main_image ? 
                                  `https://fftestservice.sarsetech.com/images/${sale.product_image || sale.product_main_image}` : undefined}
                                variant="rounded"
                                sx={{ width: 48, height: 48, bgcolor: '#f1f2f4' }}
                              >
                                {(sale.product_name || sale.prod_name)?.[0] || '?'}
                              </Avatar>
                              <Box sx={{ flex: 1 }}>
                                <Typography variant="body2" sx={{ fontWeight: 600, color: '#202223' }}>
                                  {sale.product_name || sale.prod_name}
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 1, mt: 0.5, flexWrap: 'wrap' }}>
                                  <Chip 
                                    label={`ID: ${sale.product_id}`}
                                    size="small"
                                    sx={{ 
                                      bgcolor: '#e3f2fd',
                                      color: '#1565c0',
                                      fontWeight: 600,
                                      fontSize: '10px'
                                    }}
                                  />
                                  <Chip 
                                    label={sale.sale_status || sale.status || 'Unknown'} 
                                    size="small"
                                    sx={{
                                      fontSize: '10px',
                                      ...getStatusColor(sale.sale_status || sale.status)
                                    }}
                                  />
                                </Box>
                                <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                                  <Typography variant="body2" color="text.secondary">
                                    {formatCurrency(sale.original_price || 0)}
                                  </Typography>
                                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#2e7d32' }}>
                                    → {formatCurrency(sale.product_price)}
                                  </Typography>
                                  {sale.original_price && sale.original_price > 0 && (
                                    <Chip
                                      label={`-${calculateDiscount(sale.original_price, parseFloat(sale.product_price))}%`}
                                      size="small"
                                      sx={{ 
                                        bgcolor: '#ffebee',
                                        color: '#c62828',
                                        fontWeight: 600,
                                        fontSize: '10px'
                                      }}
                                    />
                                  )}
                                </Box>
                              </Box>
                            </Box>
                            <Box sx={{ display: 'flex', gap: 0.5 }}>
                              <IconButton
                                size="small"
                                onClick={() => handleDelete(sale.id)}
                                disabled={isDeleting}
                                sx={{ 
                                  color: '#6d7175',
                                  '&:hover': { bgcolor: '#f1f2f4' }
                                }}
                              >
                                <Delete fontSize="small" />
                              </IconButton>
                            </Box>
                          </>
                        )}
                      </Box>
                    ))}
                  </Box>
                )}
              </Card>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Add Flash Sale Dialog */}
      <Dialog
        open={openAddDialog}
        onClose={handleCloseAddDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '16px',
          }
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h6" sx={{ fontWeight: 600, color: '#202223' }}>
              Add Flash Sale
            </Typography>
            <IconButton onClick={handleCloseAddDialog} size="small">
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Autocomplete
              options={products}
              getOptionLabel={(option) => `${option.prod_name} (ID: ${option.product_id || option.id})`}
              value={selectedProduct}
              onChange={handleProductSelect}
              onInputChange={(event, newInputValue) => {
                setProductSearchStr(newInputValue);
              }}
              loading={isLoadingProducts}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Search & Select Product *"
                  error={!!formErrors.product_id}
                  helperText={formErrors.product_id || 'Type to search for products'}
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <>
                        <InputAdornment position="start">
                          <Search />
                        </InputAdornment>
                        {params.InputProps.startAdornment}
                      </>
                    ),
                    endAdornment: (
                      <>
                        {isLoadingProducts ? <CircularProgress color="inherit" size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
              renderOption={(props, option) => (
                <Box component="li" {...props}>
                  <Avatar
                    src={option.product_main_image ? `https://fftestservice.sarsetech.com/images/${option.product_main_image}` : undefined}
                    variant="rounded"
                    sx={{ width: 40, height: 40, mr: 2 }}
                  >
                    {option.prod_short_name?.[0] || option.prod_name?.[0] || 'P'}
                  </Avatar>
                  <Box>
                    <Typography variant="body2">{option.prod_name}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      ID: {option.product_id || option.id} | Price: {formatCurrency(option.product_price || 0)}
                    </Typography>
                  </Box>
                </Box>
              )}
              noOptionsText="No products found. Try a different search term."
            />

            {selectedProduct && (
              <Card sx={{ borderRadius: '12px', border: '1px solid #e1e3e5', p: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: '#202223' }}>
                  Selected Product:
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Avatar
                    src={selectedProduct.product_main_image ? `https://fftestservice.sarsetech.com/images/${selectedProduct.product_main_image}` : undefined}
                    variant="rounded"
                    sx={{ width: 60, height: 60 }}
                  >
                    {selectedProduct.prod_short_name?.[0] || selectedProduct.prod_name?.[0] || 'P'}
                  </Avatar>
                  <Box>
                    <Typography variant="body2" fontWeight="bold">
                      {selectedProduct.prod_name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Price: {formatCurrency(selectedProduct.product_price || 0)}
                    </Typography>
                  </Box>
                </Box>
              </Card>
            )}
            
            <TextField
              label="Start Date *"
              name="start_date"
              type="datetime-local"
              fullWidth
              value={formData.start_date}
              onChange={handleInputChange}
              error={!!formErrors.start_date}
              helperText={formErrors.start_date}
              InputLabelProps={{
                shrink: true,
              }}
            />
            
            <TextField
              label="End Date *"
              name="end_date"
              type="datetime-local"
              fullWidth
              value={formData.end_date}
              onChange={handleInputChange}
              error={!!formErrors.end_date}
              helperText={formErrors.end_date}
              InputLabelProps={{
                shrink: true,
              }}
            />

            <FormControlLabel
              control={
                <Switch
                  checked={formData.active}
                  onChange={handleActiveChange}
                  color="primary"
                />
              }
              label="Active"
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 1 }}>
          <Button
            onClick={handleCloseAddDialog}
            disabled={isAdding}
            sx={{ 
              textTransform: 'none',
              fontWeight: 600,
              color: '#202223',
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleAddFlashSale} 
            variant="contained"
            disabled={isAdding || !selectedProduct}
            sx={{
              bgcolor: '#008060',
              textTransform: 'none',
              fontWeight: 600,
              '&:hover': { bgcolor: '#006e52' }
            }}
          >
            {isAdding ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={20} color="inherit" />
                Adding...
              </Box>
            ) : (
              'Add Flash Sale'
            )}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default FlashSale;